import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function timeToPercent(t) {
  const [h, m] = t.split(":").map(Number);
  return ((h * 60 + m) / 1440) * 100;
}

const ZONE_HUES = [220, 340, 30, 160, 280, 200, 100, 0];
function zoneColor(layout, zoneId) {
  if (!layout) return "#71717a";
  const idx = layout.zones.findIndex((z) => z.id === zoneId);
  const h = ZONE_HUES[(idx + ZONE_HUES.length) % ZONE_HUES.length];
  return `hsl(${h} 70% 45%)`;
}

function Timeline({ layout, items, compact = false }) {
  if (!layout) {
    return <div className="text-xs text-zinc-500 italic">Pick a layout to see the timeline.</div>;
  }
  const hourTicks = Array.from({ length: 9 }, (_, i) => i * 3);
  return (
    <div className="w-full" data-testid="schedule-timeline">
      <div className="relative h-5 ml-24 border-b border-zinc-200 mb-1">
        {hourTicks.map((h) => (
          <div key={h} className="absolute top-0 -translate-x-1/2 text-[10px] font-mono-disp text-zinc-500" style={{ left: `${(h / 24) * 100}%` }}>
            {String(h).padStart(2, "0")}
          </div>
        ))}
      </div>
      {layout.zones.map((zone) => {
        const slots = items.filter((it) => it.zone_id === zone.id);
        const color = zoneColor(layout, zone.id);
        return (
          <div key={zone.id} className={`flex items-stretch ${compact ? "h-6" : "h-8"} mb-1`}>
            <div className="w-24 pr-3 text-xs font-medium truncate flex items-center" title={zone.name}>
              <span className="inline-block h-2 w-2 mr-2" style={{ background: color }} />
              <span className="truncate">{zone.name}</span>
            </div>
            <div className="flex-1 relative bg-zinc-100 border-l border-zinc-200">
              {[3, 6, 9, 12, 15, 18, 21].map((h) => (
                <div key={h} className="absolute top-0 bottom-0 border-l border-zinc-200/70" style={{ left: `${(h / 24) * 100}%` }} />
              ))}
              {slots.map((s, i) => {
                const left = timeToPercent(s.start_time);
                const width = Math.max(0.5, timeToPercent(s.end_time) - left);
                const daysStr = s.days.length === 7 ? "Daily" : s.days.map((d) => DAYS[d]).join(" ");
                return (
                  <div
                    key={i}
                    title={`${s.start_time} – ${s.end_time} · ${daysStr} · ${s.media_ids.length} item(s)`}
                    className="absolute top-0 bottom-0 flex items-center px-1 text-[9px] font-mono-disp text-white truncate"
                    style={{ left: `${left}%`, width: `${width}%`, background: color, opacity: 0.9 }}
                  >
                    {!compact && (
                      <span className="truncate">
                        {s.start_time}–{s.end_time} · {s.media_ids.length}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function newItem(zoneId) {
  return {
    zone_id: zoneId,
    media_ids: [],
    days: [0, 1, 2, 3, 4, 5, 6],
    start_time: "08:00",
    end_time: "20:00",
    duration_sec: 8,
    transition: "fade",
  };
}

function Editor({ initial, layouts, media, onCancel, onSave }) {
  const empty = {
    name: "Untitled Schedule",
    layout_id: layouts[0]?.id || "",
    active: true,
    items: [],
  };
  const [d, setD] = useState(initial || empty);
  const layout = layouts.find((l) => l.id === d.layout_id);

  const setItem = (i, patch) => {
    const items = [...d.items];
    items[i] = { ...items[i], ...patch };
    setD({ ...d, items });
  };
  const addItem = () => layout && setD({ ...d, items: [...d.items, newItem(layout.zones[0]?.id || "")] });
  const rmItem = (i) => setD({ ...d, items: d.items.filter((_, idx) => idx !== i) });

  const toggleDay = (i, day) => {
    const cur = new Set(d.items[i].days);
    cur.has(day) ? cur.delete(day) : cur.add(day);
    setItem(i, { days: Array.from(cur).sort() });
  };
  const toggleMedia = (i, mid) => {
    const cur = new Set(d.items[i].media_ids);
    cur.has(mid) ? cur.delete(mid) : cur.add(mid);
    setItem(i, { media_ids: Array.from(cur) });
  };

  return (
    <div className="signage-card p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <Input
          data-testid="schedule-name-input"
          value={d.name}
          onChange={(e) => setD({ ...d, name: e.target.value })}
          className="text-xl font-display font-bold rounded-none max-w-md h-10"
        />
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Switch checked={d.active} onCheckedChange={(v) => setD({ ...d, active: v })} data-testid="schedule-active-toggle" />
            <span>{d.active ? "Active" : "Inactive"}</span>
          </div>
          <button onClick={onCancel} data-testid="schedule-cancel" className="signage-btn-secondary flex items-center gap-1"><X className="h-4 w-4" />Cancel</button>
          <button onClick={() => onSave(d)} data-testid="schedule-save" className="signage-btn-primary flex items-center gap-1"><Save className="h-4 w-4" />Save</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <Label className="label-eyebrow">Layout</Label>
          <Select value={d.layout_id} onValueChange={(v) => setD({ ...d, layout_id: v, items: [] })}>
            <SelectTrigger data-testid="schedule-layout-select" className="rounded-none mt-2 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {layouts.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="label-eyebrow">Time slots</p>
        <button data-testid="add-schedule-item" onClick={addItem} disabled={!layout} className="signage-btn-secondary flex items-center gap-1 text-xs disabled:opacity-50">
          <Plus className="h-3 w-3" />Add slot
        </button>
      </div>

      {layout && d.items.length > 0 && (
        <div className="mb-5 p-4 bg-zinc-50 border border-zinc-200">
          <p className="label-eyebrow mb-3">Live timeline · 24h</p>
          <Timeline layout={layout} items={d.items} />
        </div>
      )}

      <div className="space-y-4">
        {d.items.map((it, i) => (
          <div key={i} className="border border-zinc-200 p-4" data-testid={`schedule-item-${i}`}>
            <div className="grid lg:grid-cols-4 gap-3 mb-3">
              <div>
                <Label className="label-eyebrow">Zone</Label>
                <Select value={it.zone_id} onValueChange={(v) => setItem(i, { zone_id: v })}>
                  <SelectTrigger className="rounded-none mt-2 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {layout?.zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Start</Label>
                <Input type="time" value={it.start_time} onChange={(e) => setItem(i, { start_time: e.target.value })} className="rounded-none mt-2 h-9" />
              </div>
              <div>
                <Label className="label-eyebrow">End</Label>
                <Input type="time" value={it.end_time} onChange={(e) => setItem(i, { end_time: e.target.value })} className="rounded-none mt-2 h-9" />
              </div>
              <div>
                <Label className="label-eyebrow">Duration (s)</Label>
                <Input type="number" min={1} value={it.duration_sec} onChange={(e) => setItem(i, { duration_sec: Number(e.target.value) })} className="rounded-none mt-2 h-9" />
              </div>
            </div>

            <div className="mb-3">
              <Label className="label-eyebrow">Days</Label>
              <div className="flex gap-1 mt-2">
                {DAYS.map((dn, idx) => (
                  <button key={idx} type="button" data-testid={`day-${i}-${idx}`} onClick={() => toggleDay(i, idx)}
                    className={`px-3 py-1 text-xs font-medium border ${it.days.includes(idx) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-300 text-zinc-600"}`}>
                    {dn}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="label-eyebrow">Content ({it.media_ids.length} selected)</Label>
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-2 mt-2 max-h-44 overflow-auto p-1">
                {media.map((m) => {
                  const on = it.media_ids.includes(m.id);
                  return (
                    <button type="button" key={m.id} data-testid={`schedule-media-${i}-${m.id}`} onClick={() => toggleMedia(i, m.id)}
                      className={`aspect-square border-2 relative overflow-hidden ${on ? "border-[#002FA7]" : "border-transparent"}`} title={m.filename}>
                      {m.type === "image" ? (
                        <img src={fileUrl(m.storage_path)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-zinc-900 text-white flex items-center justify-center text-[10px]">VIDEO</div>
                      )}
                      {on && <div className="absolute inset-0 bg-[#002FA7]/30" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-3 flex justify-end">
              <button onClick={() => rmItem(i)} className="p-1.5 hover:bg-red-50 text-red-600" data-testid={`remove-schedule-item-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {d.items.length === 0 && (
          <p className="text-sm text-zinc-500 text-center py-6">Add a time slot to assign content to a zone.</p>
        )}
      </div>
    </div>
  );
}

export default function Schedules() {
  const [items, setItems] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [media, setMedia] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [s, l, m] = await Promise.all([api.get("/schedules"), api.get("/layouts"), api.get("/media")]);
      setItems(s.data); setLayouts(l.data); setMedia(m.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (draft) => {
    const payload = { name: draft.name, layout_id: draft.layout_id, active: draft.active, items: draft.items };
    try {
      if (draft.id) await api.put(`/schedules/${draft.id}`, payload);
      else await api.post("/schedules", payload);
      toast.success("Schedule saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/schedules/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Schedules</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none mt-2">Time-of-day playlists.</h1>
        </div>
        {!editing && (
          <button data-testid="new-schedule-button" onClick={() => setEditing("new")} className="signage-btn-primary flex items-center gap-2" disabled={!layouts.length}>
            <Plus className="h-4 w-4" />New schedule
          </button>
        )}
      </div>

      {!layouts.length && (
        <div className="signage-card p-6 mb-6 text-sm text-zinc-600">
          Create at least one layout first to build schedules.
        </div>
      )}

      {editing && (
        <Editor initial={editing === "new" ? null : editing} layouts={layouts} media={media} onCancel={() => setEditing(null)} onSave={save} />
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((s) => {
          const l = layouts.find((x) => x.id === s.layout_id);
          return (
            <div key={s.id} className="signage-card p-5" data-testid={`schedule-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display font-bold tracking-tight">{s.name}</h3>
                  <p className="text-xs text-zinc-500 mt-1">{l?.name || "—"} · {s.items.length} slots</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${s.active ? "bg-green-50 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {s.active ? "Active" : "Off"}
                </span>
              </div>
              {l && s.items.length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-200">
                  <Timeline layout={l} items={s.items} compact />
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <button data-testid={`edit-schedule-${s.id}`} onClick={() => setEditing(s)} className="signage-btn-secondary text-xs flex-1">Edit</button>
                <button data-testid={`delete-schedule-${s.id}`} onClick={() => remove(s.id)} className="signage-btn-secondary text-xs px-3">
                  <Trash2 className="h-3.5 w-3.5 text-red-600" />
                </button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && !editing && (
          <div className="signage-card p-10 col-span-full text-center text-zinc-500">No schedules yet.</div>
        )}
      </div>
    </div>
  );
}
